<template>
  <section class="bg-white w-screen pt-10 pb-16 overflow-hidden">
    <!-- Header -->
    <BaseContainer>
      <header
        v-if="!props.hideTitle"
        class="pb-10 space-y-2 flex justify-between items-center gap-[10px] md:gap-[20px]"
      >
        <h2
          class="text-2xl font-bold md:font-normal md:text-5xl md:leading-normal flex-1 whitespace-nowrap"
        >
          {{ $t('pages.blogs.blogPost') }}
        </h2>
        <div class="bg-black h-[4px] w-full translate-y-[-2px]" />
      </header>
    </BaseContainer>

    <!-- Content -->
    <BaseContainer
      :class="
        twJoin(
          'px-[50px] min-[1330px]:px-5 overflow-hidden',
          props.showNavigateDesktop && 'overflow-visible'
        )
      "
    >
      <div class="relative">
        <!-- Arrow -->
        <div
          :class="
            twJoin(
              '!hidden md:!flex social-c-button-prev absolute left-[0px] min-[1330px]:-left-[0px] top-[50%] -translate-x-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded bg-black text-white cursor-pointer z-10',
              props.showNavigateDesktop && '!flex max-md:hidden'
            )
          "
        >
          <Icon name="arrow-left" />
        </div>

        <!-- Swiper -->
        <nuxt-error-boundary @error="() => {}">
          <Swiper
            class="!flex md:!hidden !overflow-visible rounded-[30px] swiperborder-content"
            :modules="[
              SwiperEffectCreative,
              SwiperEffectCoverflow,
              SwiperNavigation
            ]"
            :navigation="{
              enabled: true,
              disabledClass: '!bg-gray-10 !text-typo-disabled',
              nextEl: '.social-c-button-next',
              prevEl: '.social-c-button-prev'
            }"
            :effect="'coverflow'"
            :grab-cursor="true"
            :centered-slides="true"
            :slides-per-view="'auto'"
            :coverflow-effect="{
              rotate: 50,
              stretch: 0,
              depth: 100,
              modifier: 1,
              slideShadows: true
            }"
          >
            <SwiperSlide v-for="(slide, index) in slides?.data" :key="index">
              <Suspense
                ><BlogReviewComponent
                  :key="index"
                  :title="slide.display_title"
                  :image-url="slide.thumbnail"
                  :href="slide.slug"
                  :publish-datetime="
                    slide.publish_datetime || slide.updated_at || ''
                  "
                  :subtitle="slide.display_subtitle"
                  :lang="locale"
              /></Suspense>
            </SwiperSlide>
          </Swiper>
        </nuxt-error-boundary>
        <nuxt-error-boundary @error="() => {}">
          <Swiper
            class="!hidden md:!flex"
            :modules="[SwiperEffectCreative, SwiperNavigation]"
            :slides-per-view="'auto'"
            :space-between="15"
            :navigation="{
              enabled: true,
              disabledClass: '!bg-gray-10 !text-typo-disabled',
              nextEl: '.social-c-button-next',
              prevEl: '.social-c-button-prev'
            }"
            :creative-effect="{
              prev: {
                shadow: false,
                translate: ['-20%', 0, -1]
              },
              next: {
                translate: ['100%', 0, 0]
              }
            }"
          >
            <SwiperSlide
              v-for="(slide, index) in slides?.data"
              :key="index"
              class="max-w-[282px]"
            >
              <Suspense
                ><BlogReviewComponent
                  :key="index"
                  :title="slide.display_title"
                  :image-url="slide.thumbnail"
                  :href="slide.slug"
                  :publish-datetime="slide.publish_datetime ?? ''"
                  :subtitle="slide.display_subtitle"
                  :lang="locale"
              /></Suspense>
            </SwiperSlide>
          </Swiper>
        </nuxt-error-boundary>

        <!-- Arrow -->
        <div
          :class="
            twJoin(
              '!hidden md:!flex social-c-button-next absolute right-[0px] min-[1330px]:-right-[0px] top-[50%] translate-x-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded bg-black text-white cursor-pointer z-10',
              props.showNavigateDesktop && '!flex max-md:hidden'
            )
          "
        >
          <Icon name="arrow-right" />
        </div>
      </div>
    </BaseContainer>
  </section>
</template>
<script lang="ts" setup>
import { twJoin } from 'tailwind-merge'
import { BlogRepository } from '~/repository/v2/blogRepository'
import type { GetBlogListResponse } from '~/types/apis/v2/getBlogList'

const BlogReviewComponent = defineAsyncComponent({
  loader: () => import('~/components/v2/BlogReview.vue')
})

const props = defineProps<{
  hideTitle?: boolean
  showNavigateDesktop?: boolean
}>()
const { locale } = useI18n()
const querySearch = ref({
  all: false,
  order_by: 'desc',
  sort_by: 'publish_datetime'
})
const { $serverApi } = useNuxtApp()
const blogRepo = BlogRepository($serverApi)
const { data: slides } = await useAsyncData<GetBlogListResponse | null>(
  'getBlogList',
  async () => {
    const response = await blogRepo.getBlogList(
      {
        ...querySearch.value,
        page: 1,
        per_page: 10
      },
      locale.value
    )

    if (response.data) return response
    return null
  },
  { server: false }
)
</script>

<style>
.swiperborder-content .swiper-slide-shadow-left,
.swiperborder-content .swiper-slide-shadow-right {
  border-radius: 30px;
}
</style>
